/**
 * The sum behind what a thing costs, written the way the shop reads it.
 *
 * OHC's own sheet says "[(7 x 226) + (3 x 240)] / (7 + 3) = 230.20000": how many were
 * bought at each price, and the average they come to. At the table they want the prices
 * to look like prices and the answer to two places, and the quantities left as plain
 * numbers, so "7 x $226.00" reads as seven of them at two hundred and twenty six dollars
 * rather than as two numbers multiplied.
 *
 * The stored text is left exactly as the sheet gave it; this only changes how it reads.
 * The answer shown is the figure the system actually holds, which was rounded up to the
 * cent, so the working and the "Costs" beside it can never disagree by a cent.
 */

import { formatMoney } from "./api";

const NUMBER = String.raw`\d+(?:\.\d+)?`;

export function workingOf(note: string, costCents: number | null): string {
  // Quantity x price: the price becomes money, the quantity stays a count.
  let text = note.replace(new RegExp(`(${NUMBER})\\s*x\\s*(${NUMBER})`, "g"), (_whole, quantity: string, price: string) => {
    return `${quantity} x $${formatMoney(Math.round(Number(price) * 100))}`;
  });
  // The answer, to two places, as the figure on the product.
  text = text.replace(new RegExp(`=\\s*(${NUMBER})\\s*$`), (_whole, answer: string) => {
    const cents = costCents ?? Math.ceil(Number((Number(answer) * 100).toFixed(6)));
    return `= $${formatMoney(cents)}`;
  });
  return text;
}

/** The batches in a cost note, "150 x 15.70 + 250 x 16.50", as rows; none when it is not that kind of note. */
export function batchesOf(note: string): { quantity: number; unitCents: number }[] {
  const rows: { quantity: number; unitCents: number }[] = [];
  for (const found of note.matchAll(new RegExp(`(${NUMBER})\\s*x\\s*(${NUMBER})`, "g"))) {
    rows.push({ quantity: Number(found[1]), unitCents: Math.round(Number(found[2]) * 100) });
  }
  return rows;
}
